import { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Tooltip } from '@/components/core/Tooltip';
import { useDialogWithDirtyCheck } from '@/hooks/useDialogWithDirtyCheck';
import { useIsMd } from '@/hooks/useMediaQuery';
import { useTranslation } from '@/hooks/useTranslation';
import { getAttributeDescription } from '@/services/utils';
import { DataModelAttribute, PersistedItem } from '@/types/DataModel';

import { AttributeComponent } from '../../AttributeComponent';
import { useAttributeLabelTranslator } from '../../hooks/useAttributeLabelTranslator';
import { FindItem } from './FindItem';

interface ReferenceItemOption extends PersistedItem {
    key?: string;
    label?: string;
}

interface AddItemDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    attribute: DataModelAttribute;
    readonly: boolean;
    placeholder: string;
    requiredAttributes: DataModelAttribute[];
    onItemAdded: (item: ReferenceItemOption) => void;
    preselectedItem?: ReferenceItemOption | null;
}

export const AddItemDialog = ({
    isOpen,
    onOpenChange,
    attribute,
    readonly,
    placeholder,
    requiredAttributes,
    onItemAdded,
    preselectedItem,
}: AddItemDialogProps) => {
    const [selectedItemInDialog, setSelectedItemInDialog] = useState<ReferenceItemOption | null>(null);
    const { t } = useTranslation();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const isMd = useIsMd();

    const methods = useForm<{ [key: string]: any }>({
        defaultValues: {}
    });
    const { getValues } = useFormContext();
    const containingItem = getValues() as PersistedItem;
    const { isDirty: isFormDirty } = methods.formState;

    const isDirty = isFormDirty || selectedItemInDialog !== null;

    const getAttributeValuesObject = () => {
        const formValues = methods.getValues();
        return requiredAttributes.reduce((acc, attr) => {
            const value = formValues[attr.name];
            if (value !== undefined && value !== null && value !== '') {
                acc[attr.name] = value;
            }
            return acc;
        }, {} as { [key: string]: any });
    };

    const handleDialogItemSelected = (item: ReferenceItemOption) => {
        setSelectedItemInDialog(item);
    };

    const handleAddItem = () => {
        if (selectedItemInDialog) {
            const combinedItem = {
                ...selectedItemInDialog,
                ...getAttributeValuesObject()
            };

            onItemAdded(combinedItem);

            // Clear the AttributeComponent values in the dialog
            requiredAttributes.forEach((attr) => {
                methods.setValue(attr.name, '');
            });

            setSelectedItemInDialog(null);
            onOpenChange(false);
        }
    };

    const {
        showDiscardModal,
        handleCancelDialog,
        handleDiscardChanges: handleDiscardChangesBase,
        handleCloseRequest,
        handleDiscardModalChange,
    } = useDialogWithDirtyCheck({
        isDirty,
        onOpenChange,
        onDiscard: () => {
            setSelectedItemInDialog(null);
            // Clear the AttributeComponent values in the dialog
            requiredAttributes.forEach((attr) => {
                methods.setValue(attr.name, '');
            });
            methods.reset();
        },
    });

    const handleCancelDialogWithCleanup = (open: boolean) => {
        if (open === false) {
            setSelectedItemInDialog(null);
            // Clear the AttributeComponent values in the dialog
            requiredAttributes.forEach((attr) => {
                methods.setValue(attr.name, '');
            });
        }
        handleCancelDialog(open);
    };

    const handleDiscardChanges = () => {
        setSelectedItemInDialog(null);
        handleDiscardChangesBase();
    };

    const handleCancelButtonClickWithCleanup = () => {
        handleCancelDialogWithCleanup(false);
    };

    // Handle keyboard Enter key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && isOpen) {
                // Check if the focused element is the combobox input
                const activeElement = document.activeElement;
                const isComboboxFocused = activeElement?.closest('.react-select__control') ||
                                        activeElement?.closest('[class*="select"]') ||
                                        activeElement?.id === 'find_item';
                if (selectedItemInDialog && !isComboboxFocused) {
                    // If item is selected and combobox is not focused, add the item
                    event.preventDefault();
                    handleAddItem();
                }
                // If no item is selected or combobox is focused, let the combobox handle Enter for selection
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, selectedItemInDialog]);

    return (
        <Dialog open={isOpen} onOpenChange={handleCancelDialogWithCleanup}>
            <DialogContent
                variant="small"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                isDirty={isDirty}
                onDiscardChanges={handleDiscardChanges}
                showDiscardModalExternal={showDiscardModal}
                onDiscardModalChange={handleDiscardModalChange}
            >
                <DialogHeader>
                    <DialogHeaderDefault onClose={handleCloseRequest}>{t('ADD_ITEM')}</DialogHeaderDefault>
                </DialogHeader>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(handleAddItem)}>
                        <DialogBody className="tw-flex tw-flex-col tw-gap-10">
                            <FindItem
                                attribute={attribute}
                                readonly={readonly}
                                placeholder={placeholder}
                                onItemSelected={handleDialogItemSelected}
                                autoSelect={false}
                                preselectedItem={preselectedItem}
                                containingItem={containingItem}
                            />
                            {requiredAttributes.map((a) => {
                                const tooltip = a.description || getAttributeDescription(a, t);
                                const label = translateAttributeLabel(a);
                                if (!isMd) {
                                    return (
                                        <div key={a.name} className="tw-flex tw-flex-col tw-gap-2">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <span className="tw-text-t-primary tw-text-t-sm lg-font-dynamic">{label}</span>
                                                <span className="tw-inline-block tw-h-5 tw-w-5">{tooltip && <Tooltip popupContent={tooltip} />}</span>
                                            </div>
                                            <AttributeComponent attribute={a} hideLabel={true} />
                                        </div>
                                    );
                                }
                                return (
                                    <div key={a.name} className="tw-flex tw-items-start tw-gap-4 tw-justify-end">
                                        <div className='tw-flex tw-items-center tw-justify-end tw-gap-2 tw-flex-shrink-0 tw-mt-1 md:tw-w-40'>
                                            <span className="tw-flex tw-items-center tw-text-t-primary tw-text-t-sm lg-font-dynamic">{label}</span>
                                            <span className="tw-inline-block tw-h-5 tw-w-5">{tooltip && <Tooltip popupContent={tooltip} />}</span>
                                        </div>
                                        <div className="tw-flex tw-items-center tw-flex-1">
                                            <div className="tw-w-full tw-justify-end">
                                                <AttributeComponent attribute={a} hideLabel={true} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </DialogBody>
                        <DialogFooter>
                            <Button id='confirm_add' onClick={methods.handleSubmit(handleAddItem)} disabled={!selectedItemInDialog}>{t('CONFIRM_ADD')}</Button>
                            <Button variant="secondary" onClick={handleCancelButtonClickWithCleanup}>{t('CANCEL')}</Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};
